import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, AlertTriangle } from "lucide-react";

/**
 * Cancel-without-refund dialog.
 * Use when the booking should be marked as cancelled but Stripe refund is either:
 *   - already done externally (via Stripe dashboard), or
 *   - not needed (no-show, courtesy, etc).
 */
export default function CancelDialog({ booking, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [markRefunded, setMarkRefunded] = useState(
    booking.payment_status === "paid"
  );
  const [submitting, setSubmitting] = useState(false);

  const isPaid = booking.payment_status === "paid";
  const isRefunded =
    booking.payment_status === "refunded" ||
    booking.payment_status === "partially_refunded";

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/bookings/${booking.id}/cancel`, {
        mark_refunded: markRefunded,
        reason: reason.trim() || undefined,
      });
      toast.success(`Booking cancelled — status: ${r.data.payment_status}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancellation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="cancel-dialog"
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-br from-red-50 to-amber-50 px-6 py-5 border-b border-[#E9E2D3]">
          <button
            data-testid="cancel-dialog-close"
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-white"
          >
            <X className="w-4 h-4 text-[#1A2B4C]" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <p className="uppercase text-xs tracking-widest text-red-700">
              Cancel booking
            </p>
          </div>
          <h3 className="font-serif text-2xl text-[#1A2B4C]">
            Ακύρωση χωρίς refund
          </h3>
          <p className="text-xs text-[#6B705C] mt-1">
            {booking.guest_name} · {booking.apartment_name} · €{booking.total_amount}
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            Αυτή η ενέργεια θα ακυρώσει την κράτηση{" "}
            <strong>χωρίς να επικοινωνήσει με το Stripe</strong>. Χρησιμοποιήστε την
            όταν:
          </p>
          <ul className="text-xs text-[#6B705C] space-y-1 ml-4 list-disc">
            <li>Έχετε ήδη κάνει refund απευθείας από το Stripe dashboard</li>
            <li>Δεν χρειάζεται refund (no-show, courtesy cancellation)</li>
            <li>Η κράτηση δεν πληρώθηκε ποτέ</li>
          </ul>

          {isRefunded && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
              Η κράτηση είναι ήδη <strong>{booking.payment_status}</strong> — το payment status θα διατηρηθεί όπως είναι.
            </div>
          )}

          {isPaid && (
            <label className="flex items-start gap-2 text-sm text-[#1A2B4C] cursor-pointer">
              <input
                type="checkbox"
                checked={markRefunded}
                onChange={(e) => setMarkRefunded(e.target.checked)}
                data-testid="cancel-mark-refunded"
                className="mt-0.5"
              />
              <span>
                <strong>Mark as refunded</strong>
                <span className="block text-xs text-[#6B705C] mt-0.5">
                  Έκανα ήδη refund από το Stripe — άλλαξε το payment_status σε "refunded"
                </span>
              </span>
            </label>
          )}

          <div>
            <label className="block text-xs uppercase tracking-widest text-[#6B705C] mb-1.5">
              Reason (optional)
            </label>
            <textarea
              data-testid="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Refunded manually via Stripe dashboard on 2026-05-30"
              rows={2}
              className="w-full bg-white border border-[#E9E2D3] rounded-lg px-3 py-2 text-sm text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-red-200"
            />
          </div>
        </div>

        <div className="px-6 py-4 bg-[#F5F2EB] flex gap-2 justify-end">
          <button
            data-testid="cancel-dialog-cancel"
            onClick={onClose}
            className="px-4 py-2 text-sm text-[#6B705C] hover:text-[#1A2B4C]"
          >
            Άκυρο
          </button>
          <button
            data-testid="cancel-dialog-submit"
            onClick={submit}
            disabled={submitting}
            className="px-5 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? "..." : "Ακύρωση κράτησης"}
          </button>
        </div>
      </div>
    </div>
  );
}
